import { Loader } from '@ophelos/ui/components/loader';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearAllWrittenAssessments } from '@/apps/customers/hooks/budgetAssessmentWrittenCache';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { bindCell, type DirectoryCell } from './cell-store';
import { fetchMyCells } from './directory';
import { marketHome, parseOperatorId, resolveCellNavigation } from './market-routing';

type CellContextValue = {
  cell: DirectoryCell;
  cells: DirectoryCell[];
  selectCell: (next: DirectoryCell) => void;
};

const CellContext = createContext<CellContextValue | null>(null);

export function useCell(): CellContextValue {
  const ctx = useContext(CellContext);
  if (!ctx) throw new Error('useCell must be used within CellProvider');
  return ctx;
}

export function CellProvider({ children }: { children: ReactNode }) {
  const { getAccessToken, organizationId, switchToOrganization, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [cells, setCells] = useState<DirectoryCell[] | null>(null);
  const [cell, setCell] = useState<DirectoryCell | null>(null);
  const [error, setError] = useState<string | null>(null);
  const boundOperatorIdRef = useRef<string | null>(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessToken();
        const list = await fetchMyCells(token);
        if (!cancelled) setCells(list);
      } catch {
        if (!cancelled) setError('Unable to load markets');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, getAccessToken]);

  useEffect(() => {
    if (!cells) return;
    if (cells.length === 0) {
      bindCell(null);
      setCell(null);
      boundOperatorIdRef.current = null;
      return;
    }
    const resolved = resolveCellNavigation(cells, location.pathname, organizationId);
    if (!resolved.cell) return;
    const next = resolved.cell;
    let cancelled = false;
    void (async () => {
      try {
        if (next.workos_organization_id && next.workos_organization_id !== organizationId) {
          await switchToOrganization({ organizationId: next.workos_organization_id });
        }
        if (cancelled) return;
        const prev = boundOperatorIdRef.current;
        if (prev !== null && prev !== next.operator_id) {
          await queryClient.cancelQueries();
          queryClient.clear();
          // Same reason as HubContext.setSelectedOperator: the query cache clear does not
          // reach sessionStorage, and the person's id is unchanged by the market switch.
          clearAllWrittenAssessments();
        }
        if (cancelled) return;
        boundOperatorIdRef.current = next.operator_id;
        bindCell(next);
        setCell(next);
        if (resolved.pathname !== location.pathname) {
          const { search, hash } = locationRef.current;
          navigate({ pathname: resolved.pathname, search, hash }, { replace: true });
        }
      } catch {
        if (!cancelled) setError('Unable to switch market');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cells, location.pathname, organizationId, switchToOrganization, queryClient, navigate]);

  const selectCell = useCallback(
    (next: DirectoryCell) => {
      if (next.operator_id === cell?.operator_id) return;
      navigate(marketHome(next.operator_id));
    },
    [cell, navigate]
  );

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="error">
              <Globe />
            </EmptyMedia>
            <EmptyTitle>Unable to load markets</EmptyTitle>
            <EmptyDescription>{error}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (cells?.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="default">
              <Globe />
            </EmptyMedia>
            <EmptyTitle>No markets</EmptyTitle>
            <EmptyDescription>Your account is not assigned to any market.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (!cell || !cells || parseOperatorId(location.pathname) !== cell.operator_id) {
    return <Loader text="Loading markets..." fullScreen />;
  }

  return <CellContext.Provider value={{ cell, cells, selectCell }}>{children}</CellContext.Provider>;
}
